import { invoiceFilterOf, isOverdueReceivable } from "../../../model/invoice-activity";
import { handlungsbedarf } from "../../../model/rechnungsliste";
import type { InvoiceView } from "../../../model/invoice-view";

/** Kennzahlen einer Währung über der Rechnungsliste (R-17) */
export interface KennzahlenJeWaehrung {
  /** offene Forderungen an Marken und Creator (offen und überfällig) */
  receivable: number;
  /** Zahl dieser offenen Forderungen */
  open: number;
  overdue: number;
  overdueCount: number;
  /** offene Auszahlungen an Creator (Creator-Rechnungen an die Agentur) */
  payout: number;
  payoutCount: number;
  attention: number;
}

export interface KennzahlenZeile {
  v: InvoiceView;
  balance: number;
}

const leer = (): KennzahlenJeWaehrung => ({ receivable: 0, open: 0, overdue: 0, overdueCount: 0, payout: 0, payoutCount: 0, attention: 0 });
const hatWert = (e: KennzahlenJeWaehrung) => !!(e.receivable || e.open || e.overdue || e.payout || e.payoutCount || e.attention);

/**
 * Prüf-Chat Nachtest (03.11.2026), Blocker R-17: Bei einer EUR-Agentur machte eine einzige CHF-Auszahlung CHF zur
 * Anzeigewährung („Ausstehend 0,00 CHF · 0 offene Rechnungen“), weil die Währungen nur alphabetisch sortiert waren. Jetzt steht
 * die Währung der Agentur immer zuerst und ist der Hauptwert, auch wenn sie gerade nichts offen hat; Fremdwährungen folgen je als
 * eigene Zeile und nur, wenn sie etwas zeigen. Die Zahlen gehen in den Filterzählern auf: offene Forderungen plus offene
 * Auszahlungen aller Währungen ergeben „Offen“ plus „Überfällig“ (ohne Belege mit Saldo 0 oder kleiner), überfällige Forderungen
 * stehen in der Kachel „Überfällig“.
 */
/** Offene Auszahlung laut Seite Auszahlungen (payout_candidates): Betrag und Währung je Beleg */
export interface AuszahlungKandidat { amount_cents: number; currency: string }

/**
 * Gesamtnachtest (03.11.2026), A: Hat die Person das Recht „Auszahlungen ausführen“, kommt die Auszahlungskachel aus derselben
 * Abfrage wie die Seite Auszahlungen (payout_candidates); sonst aus den Rechnungen mit derselben Regel (invoiceFilterOf).
 */
export function rechnungsKennzahlen(rows: readonly KennzahlenZeile[], orgCurrency: string, auszahlungen?: readonly AuszahlungKandidat[] | null): [string, KennzahlenJeWaehrung][] {
  const out = new Map<string, KennzahlenJeWaehrung>([[orgCurrency, leer()]]);
  for (const r of rows) {
    const f = invoiceFilterOf(r.v);
    const cur = r.v.row.currency || orgCurrency;
    const e = out.get(cur) ?? leer();
    // R-17 (Prüf-Chat 03.11.2026): dieselbe Menge wie die Filter „Offen“ und „Überfällig“ (Zahl = Filterzähler, Betrag = Saldo).
    // Stornos, stornierte und ausgezahlte Belege stehen dort nicht mehr (invoiceFilterOf); die Auszahlungskachel folgt damit der
    // Regel der Seite Auszahlungen (payout_candidates: freigegeben oder im Zahlungslauf, nicht ausgezahlt, Saldo offen)
    if (f === "offen" || f === "ueberfaellig") {
      const b = Math.max(0, r.balance);
      if (r.v.row.kind === "talent_to_agency") { if (!auszahlungen) { e.payout += b; e.payoutCount += 1; } } else { e.receivable += b; e.open += 1; }
      if (isOverdueReceivable(r.v)) { e.overdue += b; e.overdueCount += 1; }
    }
    if (handlungsbedarf(r.v)) e.attention += 1;
    out.set(cur, e);
  }
  for (const k of auszahlungen ?? []) {
    const cur = k.currency || orgCurrency;
    const e = out.get(cur) ?? leer();
    e.payout += Math.max(0, k.amount_cents ?? 0);
    e.payoutCount += 1;
    out.set(cur, e);
  }
  const haupt: [string, KennzahlenJeWaehrung] = [orgCurrency, out.get(orgCurrency)!];
  const fremd = [...out.entries()].filter(([c, e]) => c !== orgCurrency && hatWert(e)).sort((a, b) => a[0].localeCompare(b[0]));
  return [haupt, ...fremd];
}
